// Login payload interface
export interface LoginPayload {
  username: string;
  password: string;
}

// Login response interface
export interface LoginResponse {
  token: string;
  refresh_token?: string;
  user_id?: string;
  user?: {
    id: string;
    email: string;
    name: string;
    role: 'HOTEL_ADMIN' | 'ALO_SUPER' | 'HOTEL_BRAND' | string;
  };
}
// Login response interface
export interface SignUpResponse {
  success: boolean;
  message: string;
  user?: {
    id: string;
    email: string;
    name: string;
    role_name: 'HOTEL_ADMIN' | 'ALO_SUPER' | 'HOTEL_BRAND' | string;
    is_active: boolean;
    created_date: string;
  };
}

// Forgot password payload interface
export interface ForgotPasswordPayload {
  username: string;
}

// Reset password payload interface
export interface ResetPasswordPayload {
  email: string;
  code: string;
  newPassword: string;
}

// Verify token payload interface
export interface VerifyTokenPayload {
  token: string;
}

// Bulk upload response interfaces
export interface BulkUploadError {
  rowNumber: number;
  errors: string[];
}

export interface BulkUploadSummary {
  totalRowsProcessed: number;
  totalParsed: number;
  created: number;
  alreadyExisting: number;
  failed: number;
  parsingErrors: number;
  validationErrors: number;
  withoutSabreId: number;
  giataVerificationEnabled: boolean;
  giataVerified: number;
}

export interface CreatedHotel {
  rowNumber: number;
  hotelId: string;
  hotelName: string;
  sabreId: string;
}

export interface BulkUploadResponse {
  success: boolean;
  message: string;
  data: {
    jobId: string;
    totalRowsParsed: number;
    validHotels: number;
    parsingErrors: BulkUploadError[];
    enterpriseId?: string | null;
  };
}

// Permission option interface
export interface PermissionOption {
  label: string;
  value: string;
}

// Tab permission interface
export interface TabPermission {
  id: string;
  name: string;
  perm: PermissionOption[];
  main_tab: string;
  value: string; // Current permission value for this tab
}

// Tab response interface
export interface TabResponse {
  success: boolean;
  tabs: TabPermission[];
}

// Hotel search interfaces
export interface SabreSearchParams {
  hotel_chain: string;
  sabre_id: string;
}

export interface GiataIdSearchParams {
  giataId: string;
}

export interface HotelData {
  giata_id: string;
  gds_sabre_id?: string;
  gds_sabre_hotel_chain_code?: string;
  gds_bcd_travel_tsh_id?: string;
  gds_galileo_apollo_id?: string;
  gds_worldspan_id?: string;
  brand?: string | null;
  chain?: string | null;
  hotel_location_name: string;
  hotel_location_address_1?: string;
  hotel_location_address_2?: string;
  hotel_location_address_3?: string;
  hotel_location_city?: string;
  hotel_location_state?: string | null;
  hotel_location_country?: string;
  hotel_location_country_code?: string;
  hotel_location_zip_code?: string;
  hotel_location_latitude?: number;
  hotel_location_longitude?: number;
  hotel_location_phone?: string;
  hotel_location_website?: string;
  extra_data?: any;
}

export interface HotelSearchResponse {
  success: boolean;
  searchType: 'sabre_gds' | 'giata_id';
  count: number;
  hotels: HotelData[];
  alreadyExists?: boolean;
  existingHotel?: {
    id: string;
    giata_id: string;
    sabre_id: string;
    chain: string;
    brand: string;
    hotel_location_name: string;
    hotel_location_city: string;
    hotel_location_country: string;
    alo_hotel_status: string;
  };
  message?: string;
  _timezone?: {
    requested: string;
    applied: boolean;
  };
}

export interface HotelSearchError {
  error: string;
  example?: Record<string, string>;
}

export interface HotelMovedResponse {
  success: false;
  error: 'HOTEL_MOVED';
  message: string;
  moved: {
    oldGiataId: string;
    newGiataId: string;
    action: string;
    searchUrl: string;
    note: string;
  };
  originalSearch: {
    giataId: string;
  };
  _timezone?: {
    requested: string;
    applied: boolean;
  };
}

export interface CreateRFPPayload {
  name: string;
}
export interface CreateRFPUserPayload {
  email: string;
  first_name: string;
  last_name: string;
  company_name: string;
  role_name: string;
}

// Location Search Interfaces
export interface Country {
  id: number;
  name: string;
  iso_code: string;
  iso_code3: string;
  href: string;
}

export interface CountrySearchResponse {
  success: boolean;
  count: number;
  data: Country[];
}

export interface Destination {
  id: number;
  giata_destination_id: number;
  destination_name: string;
  country_id: number;
  country_code: string;
  country_name: string;
  href: string;
}

export interface DestinationSearchResponse {
  success: boolean;
  count: number;
  filters: {
    country_id: number;
    name: string;
  };
  data: Destination[];
}

export interface City {
  id: number;
  giata_city_id: number;
  city_name: string;
  iata_codes: string;
  destination_id: number;
  destination_name: string;
  giata_destination_id: number;
  country_id: number;
  country_code: string;
  country_name: string;
  href: string;
}

export interface CitySearchResponse {
  success: boolean;
  count: number;
  filters: {
    country_id: number;
    destination_id?: number;
    name: string;
  };
  data: City[];
}

export interface CountrySearchParams {
  name: string;
}

export interface DestinationSearchParams {
  country_id: number;
  name: string;
}

export interface CitySearchParams {
  country_id: number;
  destination_id?: number;
  name: string;
}

// Type for API endpoints
export type ApiEndpoints = typeof API_ENDPOINTS;

export const API_ENDPOINTS = {
  // Authentication
  auth: {
    login: '/auth/login',
    signup: '/auth/register',
    logout: '/auth/logout',
    refresh: '/auth/refresh',
    profile: '/auth/profile',
    changePassword: '/auth/complete-new-password',
    forgotPassword: '/auth/forgot-password',
    verifyToken: '/auth/verify-reset-token',
    resetPassword: '/auth/reset-password',
    permissions: '/auth/permissions',
    resendTemporaryPassword: '/auth/resend-temporary-password',
    ssoCallback: '/auth/sso/callback',
  },
  //users
  users: {
    list: '/users', //get
    create: '/users', //post
    detail: (id: string) => `/users/${id}`,
    update: (id: string) => `/users/${id}`,
    delete: (id: string) => `/users/${id}`,
    activateUser: (id: string) => `/users/${id}/activate`,
    deactivateUser: (id: string) => `/users/${id}/deactivate`,
    getRoles: '/users/roles',
    agreeTerms: '/users/me/agree-to-terms',
    disagreeTerms: '/users/me/disagree-to-terms',
  },
  // Hotels
  hotels: {
    list: '/hotels',
    links: (id: string) => `/hotels/${id}/hotelLinks`,
    updateHotelDetails: (id: string) => `/hotels/${id}`, //put
    acquire: `/hotels/acquire`,
    uploadFiles: `/hotels/upload`,
    deleteFile: `/hotels/`,
    import: '/hotels/import',
    sampleQuestionnaire: '/hotels/SampleQuestionnaire',
    searchBySabre: '/hotels/giata/search/sabre',
    searchByGiataId: '/hotels/giata/search/id',
    searchCountries: '/hotels/giata/locations/countries/search',
    searchDestinations: '/hotels/giata/locations/destinations/search',
    searchCities: '/hotels/giata/locations/cities/search',
    searchMultipleHotels: `/hotels/giata/search/multi`,
    createHotel: '/hotels/create',
    bulkCreate: '/hotels/bulk-upload',
    downloadBulkHotelTemplate: '/bulkHotelTemplate',
    exportHotels: '/hotels/export/HotelsSummary',
    benchmark: (hotelId: string) => `/hotels/${hotelId}/benchmark`,
    downloadBenchmarkReport: (hotelId: string) =>
      `/hotels/${hotelId}/benchmark/download`,
    deleteAttachment: (attachmentId: string) =>
      `/hotels/attachment/${attachmentId}`,
    clearAttachments: (hotelId: string, questionId: string) =>
      `/hotels/${hotelId}/questionAttachments/${questionId}`,
    detail: (id: string) => `/hotels/${id}`,
    steps: (hotelId: string, isFasttrack?: boolean) =>
      `/hotels/${hotelId}/sections${isFasttrack ? '?is_fasttrack=true' : ''}`,
    setQuestionnaireType: (hotelId: string) =>
      `/hotels/${hotelId}/setQuestionnaireType`,
    questionnaire: (
      hotelId: string,
      stepKey: string,
      submissionId?: string,
      isFasttrack?: boolean
    ) => {
      const params: string[] = [];
      if (submissionId) params.push(`submission_id=${submissionId}`);
      if (isFasttrack) params.push(`is_fasttrack=true`);
      return `/hotels/${hotelId}/questionnaire/${stepKey}${params.length ? `?${params.join('&')}` : ''}`;
    },
    saveAnswers: (hotelId: string, stepKey: string, isFasttrack?: boolean) =>
      `/hotels/${hotelId}/questionnaire/${stepKey}/answers${isFasttrack ? '?is_fasttrack=true' : ''}`,
    sources: (hotelId: string) => `/hotels/${hotelId}/sources`,
    saveSources: (hotelId: string) => `/hotels/${hotelId}/sources/review`,
    uploadDocument: (hotelId: string, stepKey: string) =>
      `/hotels/${hotelId}/questionnaire/${stepKey}/upload`,
    submitQuestionnaire: (hotelId: string, isFasttrack?: boolean) =>
      `/hotels/${hotelId}/submitForm${isFasttrack ? '?is_fasttrack=true' : ''}`,
    reOpenSubmission: (hotelId: string) =>
      `/hotels/${hotelId}/reopenSubmission`,
    submissions: (hotelId: string, isFasttrack?: boolean) =>
      `/hotels/${hotelId}/getSubmissions${isFasttrack ? '?is_fasttrack=true' : ''}`,
  },
  hotelOrgs: {
    list: '/hotelOrganizations',
    create: '/hotelOrganizations',
    update: (id: string) => `/hotelOrganizations/${id}`,
    delete: (id: string) => `/hotelOrganizations/${id}`,
    assignedHotels: (id: string) => `/hotelOrganizations/${id}/AssignedHotels`,
    unassignedHotels: (id: string) =>
      `/hotelOrganizations/${id}/UnassignedHotels`,
    assign: (id: string) => `/hotelOrganizations/${id}/AssignHotels`,
    remove: (id: string) => `/hotelOrganizations/${id}/UnassignHotels`,
    moveToAnotherOrg: (organization_id: string, new_org_id: string) =>
      `/hotelOrganizations/${organization_id}/moveHotels/${new_org_id}`,
    createNewUser: (id: string) => `/hotelOrganizations/${id}/createNewUser`,
    linkUser: (id: string) => `/hotelOrganizations/${id}/linkUser`,
    searchUsers: (id: string, search: string) =>
      `/hotelOrganizations/${id}/searchUsers?search=${encodeURIComponent(
        search
      )}`,
    uploadConsolidatedSubmission: (id: string) =>
      `/hotelOrganizations/${id}/uploadConsolidatedSubmission`,
    generateTemplate: (id: string) =>
      `/hotelOrganizations/${id}/generateConsolidatedTemplate`,
    getLinkedUsers: (id: string) => `/hotelOrganizations/${id}/users`,
    deleteUserFromOrg: (orgId: string) => `/hotelOrganizations/${orgId}/users`,
  },
  hotelBrands: {
    list: '/brands',
    allBrands: '/brands/brand-links',
    createBrand: '/brands', //post
    detail: (id: string) => `/brands/${id}`,
    updateBrandName: (id: string) => `/brands/${id}`, //put
    deleteBrand: (id: string) => `/brands/${id}`, //delete
    assignedHotels: (id: string) => `/brands/${id}/AssignedHotels`,
    unassignedHotels: (id: string) => `/brands/${id}/UnassignedHotels`,
    assign: (id: string) => `/brands/${id}/AssignHotels`,
    remove: (id: string) => `/brands/${id}/UnassignHotels`,
    moveToAnotherBrand: (brandId: string, newBrandId: string) =>
      `/brands/${brandId}/hotels/move/${newBrandId}`,
    getLinkedUsers: (id: string) => `/brands/${id}/users`,
    searchUsers: (id: string) => `/brands/${id}/searchUsers`,
    linkUser: (id: string) => `/brands/${id}/linkUser`,
    deleteUser: (id: string) => `/brands/${id}/users`,
  },
  hotelBrandAdmin: {
    linkedBrands: '/BrandAdmin/linkedBrands',
    brandSummary: `BrandAdmin/brandsummary`,
    enterpriseList: `BrandAdmin/BrandEnterprisesList`,
    brandHotels: `BrandAdmin/AssignedHotels`,
    enterpriseSummary: (enterpriseId: string) =>
      `BrandAdmin/EnterpriseSummary/${enterpriseId}`,
    enterpriseHotelsList: (enterpriseId: string) =>
      `BrandAdmin/EnterpriseHotels/${enterpriseId}`,
    getHotelData: (hotelId: string) => `BrandAdmin/Hotel/${hotelId}`,
    getHotelTravelFacingData: (hotelId: string) =>
      `BrandAdmin/hotel/${hotelId}/travelFacingPage`,
    reporting: `reporting/brand/reporting-insights`,
    downloadReportingCSV: `reporting/brand/reporting-insights/csv`,
    brandCountries: `/BrandAdmin/countries`,
    markets: (country_code: string) =>
      `/BrandAdmin/Countries/${country_code}/Markets`,
  },
  staticPages: {
    terms: '/legal/terms',
    downloadTerms: '/legal/terms/current/pdf',
  },
  gbta: {
    getAttachment: (hotelId: string) => `/hotels/${hotelId}/GBTA-data`,
    template: '/gbta_template',
    upload: (hotelId: string) => `/hotels/${hotelId}/GBTA-data`,
    deleteAttachment: (hotelId: string, attachmentId: string) =>
      `/hotels/${hotelId}/GBTA-data/${attachmentId}`,
  },
  travellerFacing: {
    getAll: (hotelId: string) => `/hotels/${hotelId}/travelFacingPage`,
    updateMissionStatement: (hotelId: string) =>
      `/hotels/${hotelId}/travelFacingPage/missionStatement`,
    uploadBrandLogo: (hotelId: string) =>
      `/hotels/${hotelId}/travelFacingPage/brandLogo`,
    uploadMarketingGallery: (hotelId: string) =>
      `/hotels/${hotelId}/travelFacingPage/marketingGallery`,
    uploadPppGallery: (hotelId: string) =>
      `/hotels/${hotelId}/travelFacingPage/pppGallery`,
    uploadPromotionalVideo: (hotelId: string) =>
      `/hotels/${hotelId}/travelFacingPage/promotionalVideo`,
    deleteAttachment: (hotelId: string, attachmentId: string) =>
      `/hotels/${hotelId}/travelPageAttachment/${attachmentId}`,
    syncImagesFromGiata: (hotelId: string) =>
      `hotels/${hotelId}/giata/images/sync`,
  },
  aloAdmin: {
    approveScore: (hotelId: string) => `/hotels/${hotelId}/ApproveScore`,
    publishScore: (hotelId: string) => `/hotels/${hotelId}/Publish`,
  },
  aloSuper: {
    calculateAloScore: (hotelId: string, isFasttrack?: boolean) =>
      `/hotels/${hotelId}/GenerateScore${isFasttrack ? '?is_fasttrack=true' : ''}`,
    releaseToHotel: (hotelId: string, isFasttrack?: boolean) =>
      `/hotels/${hotelId}/ReleaseToHotel${isFasttrack ? '?is_fasttrack=true' : ''}`,
  },
  subscriptions: {
    getPlans: '/subscriptions',
    getHotelSubscriptions: '/subscriptions/hotel',
  },
  payment: {
    getPaymentLink: '/payments/getPaymentUrl',
    verifyPayment: '/payments/verifyPayment',
  },
  rfp: {
    validateRFPLink: (rfpLinkId: string) =>
      `/hotels/rfp-link/${rfpLinkId}/validate`,
    createRFPUser: (rfpLinkId: string) => `/hotels/rfp-link/${rfpLinkId}/users`, //submit endpoint for consent form
    verifyOTP: (rfpLinkId: string) =>
      `/hotels/rfp-link/${rfpLinkId}/authenticate`,
    hotelData: (rfpLinkId: string) => `hotels/rfp-link/${rfpLinkId}`,
    // createRFPLink: (hotelId : string)=> `/hotels/${hotelId}/rfp-links`, //post
    createRFPLink: (hotelId: string) => `/hotels/rfp-links/${hotelId}/v2`, //post
    getRFPLinks: (hotelId: string) => `/hotels/${hotelId}/rfp-links`, //get
    deleteRFPLink: (hotelId: string, rfpLinkId: string) =>
      `/hotels/${hotelId}/rfp-links/${rfpLinkId}`, //delete
    updateRFPLink: (hotelId: string, rfpLinkId: string) =>
      `/hotels/${hotelId}/rfp-links/${rfpLinkId}`, //put
  },
  enterprises: {
    list: '/enterprises',
    quarters: '/enterprises/quarters',
    search: (searchTerm: string) =>
      `/enterprises/search?search=${encodeURIComponent(searchTerm)}`,
    detail: (id: string) => `/enterprises/${id}`,
    create: '/enterprises',
    update: (id: string) => `/enterprises/${id}`,
    delete: (id: string) => `/enterprises/${id}`,
    assignedHotels: (id: string) => `/enterprises/${id}/AssignedHotels`,
    unassignedHotels: (id: string) => `/enterprises/${id}/UnassignedHotels`,
    assign: (id: string) => `/enterprises/${id}/AssignHotels`,
    remove: (id: string) => `/enterprises/${id}/UnassignHotels`,
    users: (id: string, search: string) =>
      `/enterprises/${id}/users${
        search ? `?search=${encodeURIComponent(search)}` : ''
      }`,
    removeUser: (enterpriseId: string) => `/enterprises/${enterpriseId}/users`,
    searchUsers: (id: string, search: string) =>
      `/enterprises/${id}/searchUsers?search=${encodeURIComponent(search)}`,
    linkUser: (id: string) => `/enterprises/${id}/linkUser`,
    syncHotelsFromEnterprise: (id: string) => `/enterprises/${id}/syncHotels`,
    syncHotelsFromFiles: (id: string) =>
      `/enterprises/${id}/syncHotelsFromFiles`,
    preferredHotelProgrammeTemplate: `/preferred_hotel_programme_template`,
    masterFileTemplate: `/master_file_template`,
  },
  parentEnterprises: {
    list: '/parentEnterprises',
    create: '/parentEnterprises',
    update: (id: string) => `/parentEnterprises/${id}`,
    delete: (id: string) => `/parentEnterprises/${id}`,
    detail: (id: string) => `/parentEnterprises/${id}`,
    assignedEnterprises: (id: string) =>
      `/parentEnterprises/${id}/AssignedEnterprises`,
    unassignedEnterprises: (id: string) =>
      `/parentEnterprises/${id}/UnassignedEnterprises`,
    assignEnterprises: (id: string) =>
      `/parentEnterprises/${id}/AssignEnterprises`,
    unassignEnterprises: (id: string) =>
      `/parentEnterprises/${id}/UnassignEnterprises`,
    linkedUsers: (id: string) => `/parentEnterprises/${id}/users`,
    linkUser: (id: string) => `/parentEnterprises/${id}/linkUser`,
    deleteUsers: (id: string) => `/parentEnterprises/${id}/users`,
    searchUsers: (id: string) => `parentEnterprises/${id}/searchUsers`,
  },
  reporting: {
    getRFPUsers: (
      page: number = 1,
      limit: number = 20,
      search: string = ''
    ) => {
      let query = `?page=${page}&limit=${limit}`;
      if (search) {
        query += `&search=${encodeURIComponent(search)}`;
      }
      return `/reporting/getPublicHotelViews${query}`;
    },
    getBoostData: `/reporting/getBoostDataInterestandRequests`,
    getPublicCatalogLeadLinks: `reporting/catalog-hotel-link-leads`,
  },
  enterpriseAdmin: {
    linkedEnterprises: `/enterpriseAdmin/linkedEnterprises`,
    marketsSummaryQuarterWise: `/enterpriseAdmin/quarterly-data/MarketsList`,
    enterpriseSummaryQuarterWise: `/enterpriseAdmin/quarterly-data/enterpriseSummary`,
    enterpriseDetails: `/enterpriseAdmin/enterpriseDetails`,
    enterpriseSummary: `/enterpriseAdmin/enterpriseSummary`,
    marketsSummary: `/enterpriseAdmin/MarketsSummary`,
    summaryReportDownload: `/enterpriseAdmin/quarterly-data/report`,
    valueMatrixData: `/enterpriseAdmin/quarterly-data/valueMatrix`,
  },
  programProperties: {
    getProgramProperties: `/enterpriseAdmin/program-properties/AssignedHotels`,
    getCountries: `/enterpriseAdmin/program-properties/countries`,
    getMarketsForCountry: (countryId: string) =>
      `/enterpriseAdmin/program-properties/countries/${countryId}/Markets`,
    marketSummary: (
      countryCode: string,
      city: string,
      state: string,
      quarterId: string
    ) =>
      `/enterpriseAdmin/program-properties/quarterly-data/marketViewSummary?quarterId=${quarterId}&country_code=${countryCode}&city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}`,
    hotelDetails: (hotelId: string, quarterId: string, enterpriseId?: string) =>
      `/enterpriseAdmin/program-properties/quarterly-data/hotel/${hotelId}?quarterId=${quarterId}${enterpriseId ? `&enterprise_id=${enterpriseId}` : ''}`,
    marketSpecificHotels: (
      countryCode: string,
      city: string,
      state: string,
      page: number,
      search: string,
      sort: string,
      limit: number = 10,
      quarterId: string
    ) =>
      `/enterpriseAdmin/program-properties/quarterly-data/marketHotels?quarterId=${quarterId}&country_code=${countryCode}&city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}&page=${page}&search=${encodeURIComponent(search)}&sort=${encodeURIComponent(sort)}&limit=${limit}`,
    marketReportDownload: `/enterpriseAdmin/program-properties/quarterly-data/market-report`,
    getTravelFacingData: (hotelId: string, enterpriseId?: string) =>
      `/enterpriseAdmin/${hotelId}/travelFacingPage${enterpriseId ? `?enterprise_id=${enterpriseId}` : ''}`,
  },
  globalDirectory: {
    getNonPreferredHotels: `/enterpriseAdmin/global-directory/getNonPreferredHotels`,
    getCountries: 'enterpriseAdmin/global-directory/countries',
    getMarketsForCountry: (countryId: string) =>
      `enterpriseAdmin/global-directory/countries/${countryId}/Markets`,
    marketSpecificHotels: (
      countryCode: string,
      city: string,
      state: string,
      page: number,
      search: string,
      sort: string,
      limit: number = 10,
      quarterId: string
    ) =>
      `/enterpriseAdmin/global-directory/quarterly-data/marketHotels?quarterId=${quarterId}&country_code=${countryCode}&city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}&page=${page}&search=${encodeURIComponent(search)}&sort=${encodeURIComponent(sort)}&limit=${limit}`,
    hotelDetails: (hotelId: string, quarterId: string, enterpriseId?: string) =>
      `/enterpriseAdmin/global-directory/quarterly-data/hotel/${hotelId}?quarterId=${quarterId}${enterpriseId ? `&enterprise_id=${enterpriseId}` : ''}`,
    boostHotel: (enterpriseId?: string) =>
      `/enterpriseAdmin/global-directory/boost-request${enterpriseId ? `?enterprise_id=${enterpriseId}` : ''}`,
  },
  enterpriseAdminReporting: {
    // getReportData: `/reporting/reporting-insights`,
    getReportData: `/reporting/quarterly-reporting-insights`,
    // downloadCSV: `/reporting/reporting-insights/csv`,
    downloadCSV: `/reporting/quarterly-reporting-insights/csv`,
    getChains: `/reporting/chainsSearch`,
  },
  maintenance: {
    uploadHotelBrandOverrideFile: '/maintenance/chainCodeOverride',
    syncData: '/maintenance/ChainCodeOverride/sync',
    impactSlides: `maintenance/impact-slides`, //get and post
    updateImpactSlide: (slideId: string) =>
      `maintenance/impact-slides/${slideId}`, //put
    deleteImpactSlide: (slideId: string) =>
      `maintenance/impact-slides/${slideId}`, //delete
    lifeCycleEventsSummary: (hotelId: string) =>
      `/maintenance/${hotelId}/lifecycle-summary`, //get
  },
  travellerCatalog: {
    impactSlides: `enterpriseAdmin/catalog/impact-slides`,
    markets: `enterpriseAdmin/catalog/markets`,
    searchHistory: `enterpriseAdmin/catalog/search-history`,
    hotels: `enterpriseAdmin/catalog/hotels`,
    marketViewSummary: `enterpriseAdmin/program-properties/quarterly-data/MarketViewSummary`,
    marketHotels: `enterpriseAdmin/program-properties/MarketHotels`,
    hotelDetails: (id: string) => () =>
      `enterpriseAdmin/program-properties/hotel/${id}`,
    travelFacingData: (hotelId: string) =>
      `enterpriseAdmin/${hotelId}/travelFacingPage`,
    hotelLinks: `enterpriseAdmin/catalog/hotel-links`,
  },
  travellerCatalogPublic: {
    validate: (linkId: string) => `catalog/hotel-guide/${linkId}/validate`, //get
    leads: (linkId: string) => `catalog/hotel-guide/${linkId}/leads`, //post
    hotelData: (linkId: string) => `catalog/hotel-guide/${linkId}/hotel`, //get
  },
} as const;
